/**
 * Fiche d'un compte (admin) : permissions données quel que soit le rôle
 * (migration 0678). « Voix des animations » ouvre la rubrique « Audios
 * animations » au compte : il enregistre les textes des animations SKILLCARDS.
 */

import { Mic } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { SpeakerSpritesEditor } from '@/features/animations/speaker-sprites-editor';
import { useSetSpeakerPermission, useUserSpeakerPermission } from '@/lib/animations/speaker';

export function UserPermissionsSection({
  userId,
  role,
  name,
}: {
  userId: string;
  /** rôle du compte : un admin peut avoir un avatar sans la permission */
  role?: string | null;
  name?: string | null;
}) {
  const { user: me } = useAuth();
  const q = useUserSpeakerPermission(userId);
  const set = useSetSpeakerPermission();
  const granted = !!q.data;
  const busy = q.isLoading || set.isPending;

  return (
    <section className="border-ink-200 space-y-3 rounded-2xl border p-4">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-base font-bold">
        <Mic className="h-5 w-5" aria-hidden /> Permissions
      </h2>
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="text-ink-900 block text-sm font-semibold" id={`speaker-${userId}`}>
            Voix des animations
          </span>
          <span className="text-ink-500 block text-xs">
            Rubrique « Audios animations » : enregistrer les textes des animations SKILLCARDS. Les
            voix vont dans le brouillon ; elles sont entendues après ta republication.
          </span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={granted}
          aria-labelledby={`speaker-${userId}`}
          disabled={busy}
          onClick={() => set.mutate({ userId, granted: !granted })}
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center disabled:opacity-50"
        >
          <span
            className={
              'relative h-6 w-11 rounded-full transition-colors ' +
              (granted ? 'bg-primary-500' : 'bg-ink-200')
            }
          >
            <span
              className={
                'absolute top-0.5 h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform ' +
                (granted ? 'translate-x-[22px]' : 'translate-x-0.5')
              }
            />
          </span>
        </button>
      </div>
      {(granted || role === 'admin') && (
        <details className="border-ink-100 border-t pt-3">
          <summary className="text-ink-900 flex min-h-11 cursor-pointer items-center text-sm font-semibold">
            Avatar animé (8 images)
          </summary>
          <div className="pt-2">
            <SpeakerSpritesEditor
              userId={userId}
              forName={me?.id === userId ? undefined : (name?.split(/\s+/)[0] ?? 'ce compte')}
            />
          </div>
        </details>
      )}
      {(set.error || q.error) && (
        <p role="alert" className="text-error-600 text-sm">
          {(set.error ?? q.error)?.message}
        </p>
      )}
    </section>
  );
}
